import { useSelector } from 'react-redux';
import { selectFeesFormData } from '../../redux-state/selectors';
import {
  FUEL_TYPE,
  TITLE_STATUS,
} from '../fees/contants'
// Kept identical to the row in utility/generateFeesReport.js so the screen and
// the download agree — see utility/vehicleTypeLabel.js.
import { vehicleTypeLabel } from '../utility/vehicleTypeLabel';
import { isRefinance } from '../utility/transactionType';
import { getEffectiveRegistrationMonths } from '../utility/registrationMonthsField';

function OutputLeftSide() {
  const feesFormData = useSelector(selectFeesFormData) || {};
  // Registration term actually quoted: the user's entry, or the per-state
  // default redux-state/fees/api.js falls back to. Kept identical to the row in
  // utility/generateFeesReport.js so the download matches the screen.
  const registrationMonths = getEffectiveRegistrationMonths(feesFormData);
  // Trade-in and rebate rows are purchase-only; see utility/transactionType.js.
  // Gated here as well as on the form, because a user who enters figures as a
  // Purchase and then switches to Refinance still has them in redux.
  const isRefi = isRefinance(feesFormData);

  return (
    <div className='flex flex-col gap-4 w-[100%]'>
      <div className='flex flex-row text-gray-700 font-bold mb-2 items-center h-11'>
        Your Information
      </div>
      <div className='bg-white p-6 rounded-xl text-sm border border-gray-300'>
        <div className='text-gray-700 mb-3 font-bold'>
          Location Information
        </div>

        <div className='flex flex-col lg:flex-row w-full gap-6 lg:gap-8 mb-6'>
          <div className='flex flex-col w-full min-w-0 text-gray-700 gap-2'>
            <div className='flex flex-row justify-between gap-2 items-start'>
              <div>
                Zip Code:
              </div>
              <div className='font-medium text-right break-words min-w-0'>
                {feesFormData?.zipCode}
              </div>
            </div>

            <div className='flex flex-row justify-between gap-2 items-start'>
              <div>
                City:
              </div>
              <div className='font-medium text-right break-words min-w-0'>
                {feesFormData?.city}
              </div>
            </div>

            <div className='flex flex-row justify-between gap-2 items-start'>
              <div>
                Outside city limits:
              </div>
              <div className='font-medium text-right break-words min-w-0'>
                {feesFormData?.outSideCityLimit ? "Yes" : "No"}
              </div>
            </div>
          </div>

          <div className='flex flex-col w-full min-w-0 text-gray-700 gap-2'>
            <div className='flex flex-row justify-between gap-2 items-start'>
              <div>
                State:
              </div>
              <div className='font-medium text-right break-words min-w-0'>
                {feesFormData?.state}
              </div>
            </div>

            <div className='flex flex-row justify-between gap-2 items-start'>
              <div>
                County:
              </div>
              <div className='font-medium text-right break-words min-w-0'>
                {feesFormData?.country}
              </div>
            </div>
          </div>
        </div>

        <div className='border-t-2 border-t-gray-100 mb-3'>
          <div className='text-gray-700 mt-6 font-bold'>
            Vehicle Information <span className='text-xs text-gray-600 font-normal ml-1'>{feesFormData?.vinCode}</span>
          </div>
        </div>

        <div className='flex flex-col lg:flex-row w-full gap-6 lg:gap-8 mb-6'>
          <div className='flex flex-col w-full min-w-0 text-gray-700 gap-2'>
            <div className='flex flex-row justify-between gap-2 items-start'>
              <div>
                Model Year:
              </div>
              <div className='font-medium text-right break-words min-w-0'>
                {feesFormData?.modelYear}
              </div>
            </div>

            {feesFormData?.make &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  Make:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {feesFormData?.make}
                </div>
              </div>
            }

            {feesFormData?.model &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  Model:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {feesFormData?.model}
                </div>
              </div>
            }

            <div className='flex flex-row justify-between gap-2 items-start'>
              <div>
                Fuel Type:
              </div>
              <div className='font-medium text-right break-words min-w-0'>
                {FUEL_TYPE[feesFormData?.fuelType]}
              </div>
            </div>

            <div className='flex flex-row justify-between gap-2 items-start'>
              <div>MSRP:</div>
              <div className='font-medium text-right break-words min-w-0'>
                {Number(feesFormData?.msrp || 0).toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                })}
              </div>
            </div>
          </div>

          <div className='flex flex-col w-full min-w-0 text-gray-700 gap-2'>
            <div className='flex flex-row justify-between gap-2 items-start'>
              <div>
                Vehicle Type:
              </div>
              <div className='font-medium text-right break-words min-w-0'>
                {vehicleTypeLabel(feesFormData?.vehicleType)}
              </div>
            </div>

            <div className='flex flex-row justify-between gap-2 items-start'>
              <div>
                Vehicle Weight:
              </div>
              <div className='font-medium text-right break-words min-w-0'>
                {Number(feesFormData?.vehicleWeight).toLocaleString()} lbs
              </div>
            </div>

            {feesFormData.gvw &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  GVW:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {Number(feesFormData?.gvw).toLocaleString()} lbs
                </div>
              </div>
            }

            {/* GVWR is what several states actually rate the registration fee
                on (see utility/stateRequirements.js), so it belongs on the
                quote record next to the two weights. */}
            {feesFormData.gvwr &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  GVWR:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {Number(feesFormData?.gvwr).toLocaleString()} lbs
                </div>
              </div>
            }

            {/* Broken-out MPG values per the ATC AGF Consumption Guide.
                Each row only renders when its value was provided/auto-filled
                so the section stays compact for vehicles where MPG data
                isn't available. */}
            {feesFormData?.cityMpg !== '' && feesFormData?.cityMpg != null &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  MPG (City):
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {feesFormData?.cityMpg}
                </div>
              </div>
            }

            {feesFormData?.highwayMpg !== '' && feesFormData?.highwayMpg != null &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  Highway MPG:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {feesFormData?.highwayMpg}
                </div>
              </div>
            }

            {feesFormData?.combinedMpg !== '' && feesFormData?.combinedMpg != null &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  Combined MPG:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {feesFormData?.combinedMpg}
                </div>
              </div>
            }

            {registrationMonths &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  Registration Months:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {registrationMonths}
                </div>
              </div>
            }
          </div>
        </div>

        <div className='border-t-2 border-t-gray-100 mb-3'>
          <div className='text-gray-700 mt-6 font-bold'>
            Transaction Information
          </div>
        </div>

        <div className='flex flex-col lg:flex-row w-full gap-6 lg:gap-8 mb-6'>
          <div className='flex flex-col w-full min-w-0 text-gray-700 gap-2'>
            <div className='flex flex-row justify-between gap-2 items-start'>
              <div>
                Transaction Type:
              </div>
              <div className='font-medium text-right break-words min-w-0'>
                {feesFormData?.transactionType}
              </div>
            </div>

            <div className='flex flex-row justify-between gap-2 items-start'>
              <div>
                Plate Transfer:
              </div>
              <div className='font-medium text-right break-words min-w-0'>
                {feesFormData?.plateTransfer ? "Yes": "No"}
              </div>
            </div>


            <div className='flex flex-row justify-between gap-2 items-start'>
              <div>
                Base Price:
              </div>
              <div className='font-medium text-right break-words min-w-0'>
                {Number(feesFormData?.basePrice || 0).toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                })}
              </div>
            </div>

            {Number(feesFormData?.docsFees) > 0 &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  Doc Fees:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {Number(feesFormData?.docsFees).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                </div>
              </div>
            }

            {Number(feesFormData?.Additions) > 0 &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  Additional Fees:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {Number(feesFormData?.Additions).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                </div>
              </div>
            }

            {Number(feesFormData?.TheftProtectionField) > 0 &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  Theft Protection:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {Number(feesFormData?.TheftProtectionField).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                </div>
              </div>
            }

            {Number(feesFormData?.DeliveryFees) > 0 &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  Delivery Fees:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {Number(feesFormData?.DeliveryFees).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                </div>
              </div>
            }

            {!isRefi && Number(feesFormData?.TradeInYear) > 0 &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  Trade In Year:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {feesFormData?.TradeInYear}
                </div>
              </div>
            }

            {!isRefi && Number(feesFormData?.ManufacturerRebate) > 0 &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  Manufacturer Rebate:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {Number(feesFormData?.ManufacturerRebate).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                </div>
              </div>
            }
          </div>

          <div className='flex flex-col w-full min-w-0 text-gray-700 gap-2'>
            <div className='flex flex-row justify-between gap-2 items-start'>
              <div>
                Plate Type:
              </div>
              <div className='font-medium text-right break-words min-w-0'>
                {feesFormData?.plateType}
              </div>
            </div>

            <div className='flex flex-row justify-between gap-2 items-start'>
              <div>
                Title Status:
              </div>
              <div className='font-medium text-right break-words min-w-0'>
                {TITLE_STATUS[feesFormData?.titleStatus]}
              </div>
            </div>

            {/* Military drives RegistrationOptions="M" in the ATC request, so
                it belongs on the summary alongside the other submitted
                inputs that can change the quoted fees. */}
            {feesFormData?.military &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  Military:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  Yes
                </div>
              </div>
            }

            {Number(feesFormData?.extendedWarranties) > 0 &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  Extended Warranties:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {Number(feesFormData?.extendedWarranties).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                </div>
              </div>
            }

            {Number(feesFormData?.ProtectionPackageField) > 0 &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  Protection Packages:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {Number(feesFormData?.ProtectionPackageField).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                </div>
              </div>
            }

            {Number(feesFormData?.InsurancePackages) > 0 &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  Insurance Packages:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {Number(feesFormData?.InsurancePackages).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                </div>
              </div>
            }

            {Number(feesFormData?.DealerServiceFees) > 0 &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  Dealer Service Fees:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {Number(feesFormData?.DealerServiceFees).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                </div>
              </div>
            }

            {!isRefi && Number(feesFormData?.TradeInValue) > 0 &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  Trade In Value:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {Number(feesFormData?.TradeInValue).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                </div>
              </div>
            }

            {Number(feesFormData?.DealerInventoryTax) > 0 &&
              <div className='flex flex-row justify-between gap-2 items-start'>
                <div>
                  Dealer Inventory Tax:
                </div>
                <div className='font-medium text-right break-words min-w-0'>
                  {Number(feesFormData?.DealerInventoryTax).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                </div>
              </div>
            }

          </div>
        </div>
      </div>
    </div>
  )
}

export default OutputLeftSide;